import { useState } from 'react';
import {
  Box, Text, VStack, HStack, Button, Input, Spinner, Icon, Heading,
} from '@chakra-ui/react';
import {
  MdGroup as HouseholdIcon,
  MdRefresh as RefreshIcon,
  MdLogout as LeaveIcon,
  MdContentCopy as CopyIcon,
  MdCheck as CheckIcon,
  MdPersonAdd as JoinIcon,
  MdAdd as CreateIcon,
} from 'react-icons/md';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RecipeService } from '@/lib/recipeService';
import { CollectionService } from '@/lib/collectionService';
import { HouseholdClient } from '@/lib/householdClient';
import { toaster } from '@/lib/toaster';
import type { Household } from '@/types/household';

export default function HouseholdSection() {
  const queryClient = useQueryClient();
  const [createName, setCreateName] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinDisplayName, setJoinDisplayName] = useState('');
  const [joinMerge, setJoinMerge] = useState<boolean | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const { data: household, isLoading } = useQuery<Household | null>({
    queryKey: ['/households/me'],
    queryFn: () => HouseholdClient.getMyHousehold(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      HouseholdClient.createHousehold({ name: createName.trim(), displayName: createDisplayName.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/households/me'] });
      toaster.create({ title: 'Household created', type: 'success' });
      setCreateName('');
      setCreateDisplayName('');
    },
    onError: (err: Error) => {
      toaster.create({ title: 'Could not create household', description: err.message, type: 'error' });
    },
  });

  const joinMutation = useMutation({
    mutationFn: () =>
      HouseholdClient.joinHousehold({ inviteCode: joinCode.trim().toUpperCase(), displayName: joinDisplayName.trim(), merge: joinMerge! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/households/me'] });
      void RecipeService.reload();
      void CollectionService.load();
      toaster.create({ title: 'Joined household', type: 'success' });
      setJoinCode('');
      setJoinDisplayName('');
      setJoinMerge(null);
    },
    onError: (err: Error) => {
      toaster.create({ title: 'Could not join household', description: err.message, type: 'error' });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => HouseholdClient.refreshInviteCode(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/households/me'] });
      toaster.create({ title: 'New invite code generated', type: 'success' });
    },
    onError: () => {
      toaster.create({ title: 'Failed to refresh code', type: 'error' });
    },
  });

  const leaveMutation = useMutation({
    mutationFn: () => HouseholdClient.leaveHousehold(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/households/me'] });
      void RecipeService.reload();
      void CollectionService.load();
      toaster.create({ title: 'You have left the household', type: 'success' });
    },
    onError: () => {
      toaster.create({ title: 'Failed to leave household', type: 'error' });
    },
  });

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      toaster.create({ title: 'Could not copy code', type: 'error' });
    }
  };

  const handleLeave = () => {
    if (confirm('Leave the household? Your recipes will return to your personal pantry.')) {
      leaveMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <Spinner size="md" color="text.muted" />
      </Box>
    );
  }

  // ── In a household ───────────────────────────────────────────────
  if (household) {
    const isExpired = new Date(household.inviteCodeExpiresAt) < new Date();

    return (
      <VStack align="stretch" gap={3}>
        {/* Household name + members */}
        <Box bg="bg.canvas" border="1px solid" borderColor="border.muted" borderRadius="16px" p={5} boxShadow="0 1px 4px rgba(0,0,0,0.07)">
          <HStack gap={3} mb={4}>
            <Box
              w="44px" h="44px" borderRadius="12px" bg="accent.secondarySubtle"
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}
            >
              <Icon as={HouseholdIcon} boxSize={5} color="accent.secondary" />
            </Box>
            <Box>
              <Heading fontFamily="heading" fontWeight="800" fontSize="18px" color="text.primary" lineHeight="1.2">
                {household.name}
              </Heading>
              <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted">
                {household.members.length} {household.members.length === 1 ? 'member' : 'members'}
              </Text>
            </Box>
          </HStack>

          <VStack align="stretch" gap={2}>
            {household.members.map(m => (
              <Box
                key={m.userId}
                px={4}
                py={3}
                bg="bg.muted"
                borderRadius="10px"
                border="1px solid"
                borderColor="border.muted"
              >
                <Text fontFamily="body" fontWeight="500" fontSize="14px" color="text.primary">
                  {m.displayName || m.userId}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Invite code */}
        <Box bg="bg.canvas" border="1px solid" borderColor="border.muted" borderRadius="16px" p={5} boxShadow="0 1px 4px rgba(0,0,0,0.07)">
          <Text fontFamily="body" fontWeight="600" fontSize="11px" color="text.muted" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
            Invite code
          </Text>

          <HStack gap={3} mb={2}>
            <Text
              fontFamily="heading"
              fontWeight="800"
              fontSize="32px"
              letterSpacing="0.2em"
              color={isExpired ? 'text.muted' : 'text.primary'}
              flex={1}
              lineHeight="1"
            >
              {household.inviteCode}
            </Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleCopyCode(household.inviteCode)}
              color="accent.secondary"
              disabled={isExpired}
              minW="36px"
              px={2}
            >
              <Icon as={codeCopied ? CheckIcon : CopyIcon} boxSize={5} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Refresh invite code"
              onClick={() => refreshMutation.mutate()}
              loading={refreshMutation.isPending}
              color="text.muted"
              minW="36px"
              px={2}
            >
              <Icon as={RefreshIcon} boxSize={5} />
            </Button>
          </HStack>

          {isExpired ? (
            <Text fontFamily="body" fontSize="12px" color="accent.emphasis">
              Expired — tap refresh to generate a new code
            </Text>
          ) : (
            <Text fontFamily="body" fontSize="12px" color="text.muted">
              Expires {new Date(household.inviteCodeExpiresAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
            </Text>
          )}

          <Text fontFamily="body" fontSize="12px" color="text.muted" mt={2}>
            When someone joins they'll be asked whether to bring their existing recipes and collections across.
          </Text>
        </Box>

        {/* Leave */}
        <Box bg="bg.canvas" border="1px solid" borderColor="border.muted" borderRadius="16px" overflow="hidden" boxShadow="0 1px 4px rgba(0,0,0,0.07)">
          <HStack
            gap={3}
            px={5}
            py={4}
            cursor="pointer"
            onClick={handleLeave}
            opacity={leaveMutation.isPending ? 0.5 : 1}
            _hover={{ bg: 'bg.muted' }}
            transition="background 0.15s"
          >
            <Box
              w="36px" h="36px" borderRadius="9px" bg="red.50"
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}
            >
              <Icon as={LeaveIcon} boxSize={4} color="red.500" />
            </Box>
            <Text fontFamily="body" fontWeight="500" fontSize="14px" color="red.500" flex={1}>
              Leave household
            </Text>
          </HStack>
        </Box>
      </VStack>
    );
  }

  // ── No household ─────────────────────────────────────────────────
  return (
    <VStack align="stretch" gap={4}>
      {/* Create card */}
      <Box bg="bg.canvas" border="1px solid" borderColor="border.muted" borderRadius="16px" p={5} boxShadow="0 1px 4px rgba(0,0,0,0.07)">
        <HStack gap={3} mb={4}>
          <Box
            w="44px" h="44px" borderRadius="12px" bg="accent.secondarySubtle"
            display="flex" alignItems="center" justifyContent="center" flexShrink={0}
          >
            <Icon as={CreateIcon} boxSize={5} color="accent.secondary" />
          </Box>
          <Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
              Create a household
            </Heading>
            <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted">
              Share your pantry with family or housemates
            </Text>
          </Box>
        </HStack>

        <VStack align="stretch" gap={3}>
          <Input
            placeholder="Household name, e.g. The Smiths"
            value={createName}
            onChange={e => setCreateName(e.target.value)}
            size="md"
            bg="bg.canvas"
            borderColor="text.muted"
            _placeholder={{ color: 'text.muted' }}
            _focus={{ borderColor: 'accent.secondary', boxShadow: '0 0 0 1px var(--chakra-colors-accent-secondary)' }}
          />
          <Input
            placeholder="Your name (shown to members)"
            value={createDisplayName}
            onChange={e => setCreateDisplayName(e.target.value)}
            size="md"
            bg="bg.canvas"
            borderColor="text.muted"
            _placeholder={{ color: 'text.muted' }}
            _focus={{ borderColor: 'accent.secondary', boxShadow: '0 0 0 1px var(--chakra-colors-accent-secondary)' }}
          />
          <Button
            onClick={() => createMutation.mutate()}
            loading={createMutation.isPending}
            disabled={!createName.trim() || !createDisplayName.trim()}
            bg="accent.emphasis"
            color="white"
            _hover={{ bg: 'accent.emphasisHover' }}
            size="md"
            w="full"
            mt={1}
          >
            Create household
          </Button>
        </VStack>
      </Box>

      {/* Divider */}
      <HStack gap={3}>
        <Box flex={1} h="1px" bg="border.muted" />
        <Text fontFamily="body" fontWeight="400" fontSize="12px" color="text.muted">or</Text>
        <Box flex={1} h="1px" bg="border.muted" />
      </HStack>

      {/* Join card */}
      <Box bg="bg.canvas" border="1px solid" borderColor="border.muted" borderRadius="16px" p={5} boxShadow="0 1px 4px rgba(0,0,0,0.07)">
        <HStack gap={3} mb={4}>
          <Box
            w="44px" h="44px" borderRadius="12px" bg="accent.secondarySubtle"
            display="flex" alignItems="center" justifyContent="center" flexShrink={0}
          >
            <Icon as={JoinIcon} boxSize={5} color="accent.secondary" />
          </Box>
          <Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
              Join with a code
            </Heading>
            <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted">
              Got an invite code? Enter it here
            </Text>
          </Box>
        </HStack>

        <VStack align="stretch" gap={3}>
          <Input
            placeholder="INVITE CODE"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            maxLength={6}
            size="md"
            bg="bg.canvas"
            borderColor="text.muted"
            _placeholder={{ color: 'text.muted' }}
            _focus={{ borderColor: 'accent.secondary', boxShadow: '0 0 0 1px var(--chakra-colors-accent-secondary)' }}
            fontFamily="heading"
            fontWeight="800"
            letterSpacing="0.15em"
            textAlign="center"
            fontSize="20px"
          />
          <Input
            placeholder="Your name (shown to members)"
            value={joinDisplayName}
            onChange={e => setJoinDisplayName(e.target.value)}
            size="md"
            bg="bg.canvas"
            borderColor="text.muted"
            _placeholder={{ color: 'text.muted' }}
            _focus={{ borderColor: 'accent.secondary', boxShadow: '0 0 0 1px var(--chakra-colors-accent-secondary)' }}
          />
          <Box>
            <Text fontFamily="body" fontWeight="600" fontSize="12px" color={joinMerge === null ? 'accent.emphasis' : 'text.muted'} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
              {joinMerge === null ? 'Select an option to continue' : 'Your recipes'}
            </Text>
            <Box
              border="1px solid"
              borderColor={joinMerge === null ? 'accent.emphasis' : 'border.muted'}
              borderRadius="12px"
              overflow="hidden"
            >
              <HStack
                gap={3}
                px={4}
                py={3}
                cursor="pointer"
                bg={joinMerge === true ? 'accent.secondarySubtle' : 'bg.canvas'}
                onClick={() => setJoinMerge(true)}
                borderBottom="1px solid"
                borderColor="border.muted"
              >
                <Box
                  w="18px" h="18px" borderRadius="full" border="2px solid"
                  borderColor={joinMerge === true ? 'accent.secondary' : 'border.muted'}
                  bg={joinMerge === true ? 'accent.secondary' : 'transparent'}
                  flexShrink={0}
                />
                <Box>
                  <Text fontFamily="body" fontWeight="600" fontSize="13px" color="text.primary">
                    Bring my recipes &amp; collections
                  </Text>
                  <Text fontFamily="body" fontWeight="300" fontSize="12px" color="text.muted">
                    Your existing recipes and collections will be shared with the household
                  </Text>
                </Box>
              </HStack>
              <HStack
                gap={3}
                px={4}
                py={3}
                cursor="pointer"
                bg={joinMerge === false ? 'accent.secondarySubtle' : 'bg.canvas'}
                onClick={() => setJoinMerge(false)}
              >
                <Box
                  w="18px" h="18px" borderRadius="full" border="2px solid"
                  borderColor={joinMerge === false ? 'accent.secondary' : 'border.muted'}
                  bg={joinMerge === false ? 'accent.secondary' : 'transparent'}
                  flexShrink={0}
                />
                <Box>
                  <Text fontFamily="body" fontWeight="600" fontSize="13px" color="text.primary">
                    Start fresh
                  </Text>
                  <Text fontFamily="body" fontWeight="300" fontSize="12px" color="text.muted">
                    Your personal recipes stay private
                  </Text>
                </Box>
              </HStack>
            </Box>
          </Box>

          <Button
            onClick={() => joinMutation.mutate()}
            loading={joinMutation.isPending}
            disabled={joinCode.trim().length < 6 || !joinDisplayName.trim() || joinMerge === null}
            bg="accent.emphasis"
            color="white"
            _hover={{ bg: 'accent.emphasisHover' }}
            size="md"
            w="full"
            mt={1}
          >
            Join household
          </Button>
        </VStack>
      </Box>
    </VStack>
  );
}
